/**
 * Brand Pulse service API.
 * Computes brand health metrics from online signals (Google Trends, Google Search,
 * Google News) plus LLM-scored sentiment. See BRAND_PULSE_METHODOLOGY.md.
 */

function getBrandPulseUrl(): string {
  const raw = import.meta.env.VITE_BRAND_PULSE_API_URL;
  const url = typeof raw === 'string' ? raw.trim() : '';
  if (url) return url;
  return 'http://localhost:4010';
}

export interface BrandPulseRequestBody {
  brand: string;
  competitors: string[];
  category: string;
  categoryQueries?: string[];
  geo?: string;
  timeframe?: string;
  /** Optional explicit Wikipedia slugs per brand (handles ambiguous names). */
  wikipediaSlugs?: Record<string, string>;
}

export interface BrandSoS {
  brand: string;
  latest: number;
  trailing4w: number;
  prior4w: number;
  momentum: number;
  series: { date: string; value: number }[];
  rawInterestSeries: { date: string; value: number }[];
  insufficientVolume: boolean;
}

export interface WeeklyPanelPoint {
  date: string;
  timestamp: number;
  values: Record<string, number>;
}

export interface CategoryFitResult {
  brand: string;
  score: number;
  appearancesByQuery: { query: string; organic: boolean; paa: boolean; knowledge: boolean }[];
}

export interface ComparisonIntentResult {
  brand: string;
  vsScore: number;
  reviewScore: number;
  bestScore: number;
  combined: number;
}

export interface SentimentResult {
  brand: string;
  nss: number;
  positive: number;
  neutral: number;
  negative: number;
  sampleSize: number;
  topHeadlines: {
    title: string;
    source?: string;
    sentiment: 'positive' | 'neutral' | 'negative';
    link?: string;
  }[];
}

export type PillarName =
  | 'awareness'
  | 'mentalAvailability'
  | 'consideration'
  | 'sentiment'
  | 'engagement';

export interface PillarBreakdown {
  /** 0–100, 50 = cohort average */
  score: number;
  /** Per-source z-scores feeding this pillar */
  sources: Record<string, number>;
  /** True when at least one source had cohort-wide variance */
  hasSignal: boolean;
}

export interface BrandPulseScore {
  brand: string;
  /** 0–100 weighted composite */
  bpsIndex: number;
  /** 0–1, fraction of pillars with non-zero variance */
  confidence: number;
  pillars: Record<PillarName, PillarBreakdown>;
  /** Weights actually applied (re-normalised across available pillars) */
  weightsApplied: Record<PillarName, number>;
}

export interface RelatedQuery {
  query: string;
  value: number;
  extracted_value?: number;
}

export interface WikipediaSignal {
  brand: string;
  article: { title: string; pageId: number; description?: string } | null;
  monthlyViews: { date: string; views: number }[];
  recentAvg: number;
  momentum: number;
}

export interface YouTubeSignal {
  brand: string;
  query: string;
  totalResults: number;
  videos: {
    videoId: string;
    title: string;
    channelTitle: string;
    publishedAt: string;
    viewCount?: number;
    likeCount?: number;
    commentCount?: number;
  }[];
  totalViews: number;
  totalLikes: number;
  totalComments: number;
}

export interface GdeltSignal {
  brand: string;
  query: string;
  tone: { date: string; tone: number }[];
  volume: { date: string; pct: number }[];
  recentTone: number;
  volumeMomentum: number;
}

export interface BrandPulseResponse {
  meta: {
    brand: string;
    competitors: string[];
    category: string;
    geo: string;
    timeframe: string;
    methodologyVersion: string;
    computedAt: string;
  };
  shareOfSearch: {
    brands: BrandSoS[];
    weeklyPanel: WeeklyPanelPoint[];
  };
  categoryFit: CategoryFitResult[];
  comparisonIntent: ComparisonIntentResult[];
  sentiment: SentimentResult[];
  compositeScores: BrandPulseScore[];
  associations: { brand: string; rising: RelatedQuery[]; top: RelatedQuery[] }[];
  serpSnapshots: unknown[];
  wikipedia: WikipediaSignal[];
  youtube: YouTubeSignal[];
  gdelt: GdeltSignal[];
  warnings: string[];
}

export interface SupportedCountry {
  code: string;
  name: string;
  region: 'north_america' | 'uk' | 'europe' | 'scandinavia' | 'south_america';
}

export interface SupportedRegion {
  key: 'north_america' | 'uk' | 'europe' | 'scandinavia' | 'south_america' | 'all';
  label: string;
  countries: string[];
}

export async function listGeos(): Promise<{ countries: SupportedCountry[]; regions: SupportedRegion[] }> {
  const res = await fetch(`${getBrandPulseUrl().replace(/\/$/, '')}/countries`);
  if (!res.ok) throw new Error(`brand-pulse /countries ${res.status}`);
  return (await res.json()) as { countries: SupportedCountry[]; regions: SupportedRegion[] };
}

export async function getBrandPulse(body: BrandPulseRequestBody): Promise<BrandPulseResponse> {
  const res = await fetch(`${getBrandPulseUrl().replace(/\/$/, '')}/brand-pulse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`brand-pulse ${res.status}: ${text.slice(0, 300)}`);
  }
  return (await res.json()) as BrandPulseResponse;
}
